import React from "react";
import styled from "styled-components";
import Colors from "../../ui/global/Colors";
import Fonts from "../../ui/global/Fonts";
import Icon from "../../ui/global/Icon";
import DropdownSimple from "../../ui/components/DropdownSimple";
import { defaultConfig } from "../../utils/config";

export interface PriceCardProps {
  $style: 'primary' | 'regular' | 'ghost';
  $colorMode: 'light' | 'dark';
}

const PriceCard = styled.div<PriceCardProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 300px;
  background-color: ${props => {
    if (props.$style === 'ghost') {
      if (props.$colorMode === 'light') return Colors.white+60;
      return Colors.grayDark+80;
    } 
    return props.$colorMode === 'light' ? Colors.white : Colors.grayDark;
  }};
  border-radius: 12px;
  border: ${props => props.$colorMode === 'light' ? `1px solid ${Colors.grey1}` 
    : `1px solid ${Colors.grey8}`};
  padding: 10px;
  gap: 16px;
  transform: ${props => props.$style === 'primary' ? 'scale(1.08)' : 'scale(1)'};
  transform-origin: center;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    transform: ${props => {
      if (props.$style === 'ghost') return 'scale(1)';
      if (props.$style === 'primary') return 'scale(1.08) translateY(-12px)';
      return 'translateY(-8px)';
    }};
    border: ${props => {
      if (props.$style !== 'ghost') 
      return props.$colorMode === 'light' 
        ? `1px solid ${Colors.grey2}` 
        : `1px solid ${Colors.grey7}`;
    }};
    box-shadow: ${props => {
      if (props.$style === 'ghost') return 'none';
      return props.$colorMode === 'light' 
        ? '0 8px 24px rgba(0, 0, 0, 0.06)' 
        : '0 8px 24px rgba(255, 255, 255, 0.12)';
    }};
  }

  // Consistent sizes on mobile when vertical
  @media (max-width: 1000px) {
    transform: scale(1);
    &:hover {
      transform: scale(1.05);
    }
  }
`;

interface TopContainerProps {
  $style: 'primary' | 'regular' | 'ghost';
  $colorMode: 'light' | 'dark';
}

const TopContainer = styled.div<TopContainerProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 8px 8px 0 0;
  background-color: ${props => {
    // if (props.$style === 'ghost') return 'transparent';
    return props.$colorMode === 'light' 
      ? Colors.grayPale 
      : Colors.black+90;
  }};
  border-bottom: 1px solid ${props => props.$colorMode === 'light' ? Colors.grayPale : Colors.grey8+90};
  padding: 12px 12px 16px 12px;
  gap: 8px;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: baseline;
`;

const PlanName = styled.h2<{ $visible: boolean; $colorMode?: 'light' | 'dark' }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: baseline;
  width: fit-content;
  height: 31px;
  border-radius: 48px;
  border: 1px solid ${props => props.$colorMode === 'light' ? Colors.grey2 : Colors.grey7};
  background-color: ${props => props.$colorMode === 'light' ? Colors.white+80 : Colors.grey9};
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 14px;
  color: ${props => props.$colorMode === 'light' ? Colors.grey8 : Colors.grey1};
  padding: 4px 16px;
  margin: 0 auto;
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
`;

const Price = styled.h1<{ $colorMode?: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendBold.fontFamily};
  font-weight: ${Fonts.lexendBold.fontWeight};
  font-size: 42px;
  line-height: 1.2em;
  color: ${props => props.$colorMode === 'light' ? Colors.black : Colors.white};
  margin: 4px;
`;

const PriceSubText = styled.span<{ $colorMode?: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendLight.fontFamily};
  font-weight: ${Fonts.lexendLight.fontWeight};
  font-size: 18px;
  line-height: 1.2em;
  color: ${props => props.$colorMode === 'light' ? Colors.grey6 : Colors.grey3};
`;

const PriceVerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0;
  margin-top: 8px;
`;

const PriceMiniText = styled.p<{ $colorMode?: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendLight.fontFamily};
  font-weight: ${Fonts.lexendLight.fontWeight};
  font-size: 14px;
  line-height: 1.2em;
  color: ${props => {
    return props.$colorMode === 'light' ? Colors.grey5 : Colors.grey5;
  }};
  margin: 0;
  text-align: center;
  white-space: pre-wrap;
`;

const ChecklistContainer = styled.div<{ $colorMode?: 'light' | 'dark' }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 8px 20px;
  margin-bottom: 14px;
  gap: 12px;
`;

const DropdownContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ChecklistItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  line-height: 1.2em;
  width: 100%;
  gap: 8px;
`;

const ItemText = styled.p<{ $colorMode?: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 16px;
  color: ${props => props.$colorMode === 'light' ? Colors.grey7 : Colors.grey3};
  margin: 0;
`;

const ButtonContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: auto 20px 5px 20px;
`;

interface ButtonProps {
  $style: 'primary' | 'regular' | 'ghost';
  $colorMode: 'light' | 'dark';
  $disabled?: boolean;
}

const Button = styled.button<ButtonProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  // width: 100%;
  background-color: ${props => {
    if (props.$colorMode === 'light') {
      switch(props.$style) {
        case 'primary': return Colors.grey9;
        case 'regular': return 'transparent';
        case 'ghost': return 'transparent';
        default: return 'transparent';
      }
    } else {
      switch(props.$style) {
        case 'primary': return Colors.grey1;
        case 'regular': return 'transparent';
        case 'ghost': return 'transparent';
        default: return 'transparent';
      }
    }
  }};
  color: ${props => {
    if (props.$colorMode === 'light') {
      return props.$style === 'primary' ? Colors.white : Colors.black;
    } else {
      return props.$style === 'primary' ? Colors.black : Colors.white;
    }
  }};
  border: ${props => {
    if (props.$style === 'ghost') return `1px solid ${Colors.grayMed+80}` ;
    return props.$colorMode === 'light' 
      ? `1px solid ${Colors.grey3}` 
      : `1px solid ${Colors.white+80}`;
  }};
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 16px;
  border-radius: 48px;
  padding: 10px 24px;
  gap: 6px;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  margin: 0 auto;
  transition: all 0.2s ease;
  opacity: ${props => props.$style === 'ghost' ? 0.7 : 1};
  
  &:hover {
    background-color: ${props => {
      if (props.$colorMode === 'light') {
        switch(props.$style) {
          case 'primary': return Colors.grey8; // Lighter than grey9
          default: return Colors.transparent;
        }
      } else {
        switch(props.$style) {
          case 'primary': return Colors.white; // Lighter than grey1
          default: return Colors.transparent;
        }
      }
    }};
  }

  &:active {
    background-color: ${props => {
      if (!props.$disabled) {
        if (props.$colorMode === 'light') {
          switch(props.$style) {
            case 'primary': return Colors.black; // Darker than grey9
            default: return Colors.grey1+80;
          }
        } else {
          switch(props.$style) {
            case 'primary': return Colors.grey2; // Darker than grey1
            default: return Colors.black+80;
          }
        }
      }
    }};
  }
`;

interface DropdownConfig {
  show: boolean;
  options: (string | { id: string; value: string })[];
  initialValue?: any;
  placeholder?: string;
  onChange?: (option: any) => void;
  position?: number; // Position in the checklist (0-based index)
}

interface PriceOptionsProps {
  planName: string;
  price: string;
  priceSubText: string;
  checklistItems: string[];
  buttonText?: string;
  style?: 'primary' | 'regular' | 'ghost';
  colorMode?: 'light' | 'dark';
  onClick?: () => void;
  disabled?: boolean;
  // Dropdown props grouped into a single object
  dropdown?: DropdownConfig;
}

const PriceOptions: React.FC<PriceOptionsProps> = ({
  planName,
  price,
  priceSubText,
  checklistItems,
  buttonText,
  style = 'ghost',
  colorMode = 'dark',
  onClick,
  disabled = false,
  // Default dropdown configuration
  dropdown = {
    show: false,
    options: [],
    initialValue: null,
    placeholder: "Select an option",
    onChange: () => {},
    position: -1, // Default to -1 means not in-line (for backward compatibility)
  },
}) => {
  return (
    <PriceCard $style={style} $colorMode={colorMode}>
      <TopContainer $style={style} $colorMode={colorMode}>
        <PlanName $visible={!!planName} $colorMode={colorMode}>{planName}</PlanName>
        <PriceVerticalContainer>
          {/* {defaultConfig.ENABLE_FREE_TRIAL && price !== 'FREE' && (
            <PriceMiniText $colorMode={colorMode}>
              20 free credits, then
            </PriceMiniText>
          )} */}
          <PriceContainer>
            <Price $colorMode={colorMode}>{price}</Price>
            <PriceSubText $colorMode={colorMode}>{priceSubText}</PriceSubText>
          </PriceContainer>
        </PriceVerticalContainer>
      </TopContainer>
      
      <ChecklistContainer $colorMode={colorMode}>
        {checklistItems.map((item, index) => {
          // Check if dropdown should be rendered at this position
          if (dropdown.show && dropdown.position === index) {
            return (
              <DropdownContainer key={`dropdown-${index}`}>
                <Icon 
                  icon="check-slim" 
                  color={colorMode === 'light' ? Colors.black : Colors.white} 
                  size={20} 
                />
                <DropdownSimple
                  options={dropdown.options}
                  initialValue={dropdown.initialValue}
                  onChange={dropdown.onChange}
                  colorMode={colorMode}
                />
              </DropdownContainer>
            );
          }
          
          // Otherwise render normal checklist item
          return (
            <ChecklistItem key={index}>
              <Icon 
                icon="check-slim" 
                color={colorMode === 'light' ? Colors.grey8 : Colors.grey2} 
                size={20} 
              />
              <ItemText $colorMode={colorMode}>{item}</ItemText>
            </ChecklistItem>
          );
        })}
      </ChecklistContainer>
      <ButtonContainer>
        {buttonText && (
          <Button 
            $style={style} 
            $colorMode={colorMode}
            $disabled={disabled}
            onClick={onClick} 
            // $disabled={disabled || style === 'ghost'}
            // onClick={disabled || style === 'ghost' ? undefined : onClick}
          >
            {buttonText}
            {(style === 'primary' || style === 'regular') && 
              <Icon 
                icon="right" 
                color={
                  colorMode === 'light'
                    ? (style === 'primary' ? Colors.white : Colors.black)
                    : (style === 'primary' ? Colors.black : Colors.white)
                } 
                size={18} 
              />
            }
          </Button>
        )}
      </ButtonContainer>
    </PriceCard>
  );
};

export default PriceOptions;